/**
 * ReviewStep — Step 4 of the Job Management Wizard
 *
 * Read-only summary of all job categories and their assigned tasks.
 */

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { useCan } from '@skewb/access-control/react';
import { CheckCircle, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCategoryTasks, useJobCategories, useTaskTypes } from '../hooks/useJobManagement';

function CategoryDetail({ categoryId }: { categoryId: number }) {
  // WRK-5670: assigned tasks are a CategoryTask read; skip the fetch the backend would 403.
  const canReadTasks = useCan('read', 'CategoryTask');
  const { data, isLoading } = useCategoryTasks(categoryId, { enabled: canReadTasks });
  const tasks = data?.data ?? [];

  if (!canReadTasks) {
    return (
      <div className="px-4 py-2 text-xs text-muted-foreground italic">
        You don't have permission to view assigned tasks
      </div>
    );
  }

  if (isLoading) {
    return <div className="px-4 py-2 text-xs text-muted-foreground">Loading tasks...</div>;
  }

  if (tasks.length === 0) {
    return <div className="px-4 py-2 text-xs text-muted-foreground italic">No tasks assigned</div>;
  }

  return (
    <div className="px-4 pb-3">
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-xs">Order</TableHead>
              <TableHead className="text-xs">Task Type</TableHead>
              <TableHead className="text-xs">Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task, idx) => (
              <TableRow key={task.id}>
                <TableCell className="text-xs">{task.orderNumber ?? idx + 1}</TableCell>
                <TableCell className="text-xs font-medium">{task.taskTypeName ?? '-'}</TableCell>
                <TableCell className="text-xs">{task.type ?? '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function ReviewStep() {
  const navigate = useNavigate();
  const { data: categoriesData, isLoading: categoriesLoading } = useJobCategories();
  const { data: taskTypesData, isLoading: taskTypesLoading } = useTaskTypes();
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const categories = categoriesData?.data ?? [];
  const taskTypes = taskTypesData?.data ?? [];

  function toggleCategory(id: number) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Layers className="w-4 h-4" style={{ color: '#E84A2B' }} />
            <span className="text-sm font-medium">Job Categories</span>
          </div>
          <span className="text-2xl font-bold">{categoriesLoading ? '-' : categories.length}</span>
        </div>
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4" style={{ color: '#E84A2B' }} />
            <span className="text-sm font-medium">Task Types</span>
          </div>
          <span className="text-2xl font-bold">{taskTypesLoading ? '-' : taskTypes.length}</span>
        </div>
      </div>

      {/* Categories with tasks */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Categories and Assigned Tasks</h3>
        <div className="border rounded-lg divide-y">
          {categoriesLoading ? (
            <div className="p-4 text-sm text-muted-foreground text-center">Loading...</div>
          ) : categories.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No job categories configured
            </div>
          ) : (
            categories.map((cat) => {
              const isExpanded = expandedCategories.has(cat.id);
              return (
                <div key={cat.id}>
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium">{cat.name ?? '-'}</span>
                      {cat.code && (
                        <span className="text-xs text-muted-foreground">({cat.code})</span>
                      )}
                    </div>
                    <Badge variant="outline">{cat.taskCount} tasks</Badge>
                  </button>
                  {isExpanded && <CategoryDetail categoryId={cat.id} />}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Done button */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={() => navigate('/settings')}
          style={{ backgroundColor: '#E84A2B' }}
          className="text-white"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
